/**
 * Wohin ein Tipp auf eine Benachrichtigung führen soll.
 *
 * **Der Server sagt es schon.** In jeder Push-Nutzlast steht eine Adresse
 * (`Notifier::payload()`): der Reise-Chat, ein Antwortverlauf, die Infoseite.
 * Der Service Worker öffnet sie beim Antippen — und das reicht, solange die
 * App geschlossen war.
 *
 * **Ist sie schon offen, reicht es auf dem iPhone nicht.** Safari kennt
 * `WindowClient.navigate` nicht; der Service Worker holt das Fenster dann nur
 * nach vorn, und es bleibt stehen, wo es war. Deshalb schickt er den Pfad
 * zusätzlich als Nachricht, und hier wird sie aufgefangen.
 *
 * **Keine zweite Wahrheit in der Nutzlast.** Es wäre verlockend, `trip_id` und
 * `with_user` mitzuschicken und den Weg hier zusammenzubauen — dann stünde die
 * Zuordnung „Antwort gehört in den Einzelverlauf" zweimal da, einmal im
 * Notifier und einmal hier, und die nächste Route trüge nur eine der beiden
 * nach. Der Client bekommt einen Pfad und springt dorthin, mehr weiß er nicht.
 */

/** Genau die Kürzung, die auch `api/client.ts` und `lib/push.ts` benutzen. */
const BASE = import.meta.env.BASE_URL.replace(/\/$/, '')

type Empfaenger = (pfad: string) => void

let empfaenger: Empfaenger | null = null

/**
 * Ein Mitschrieb, **weil dreimal Raten genug ist.**
 *
 * Aus dem Betrieb, zum zweiten Mal: „der Absprung aus der Push-Nachricht in
 * die Anfragen funktioniert nicht." Im Labor messen beide Wege grün, und das
 * heißt nicht, dass sie gehen — es heißt, dass das Labor die falsche Frage
 * stellt. Dieselbe Lage wie beim Sendeknopf (`lib/sendeprotokoll.ts`), und
 * dieselbe Antwort: nicht noch eine Vermutung, sondern ein Satz vom Gerät.
 *
 * Aufgeschrieben wird jeder Schritt des Sprungs: was der Worker geschickt hat,
 * was daraus wurde, ob jemand zugehört hat, und was die Nachfrage beim Aufbau
 * ergeben hat. Angesehen wird es in *Einstellungen → Diagnose*.
 *
 * **Nur im Speicher, nichts geht hinaus.** Ein Neustart der Anwendung leert
 * es.
 */
export interface Sprungschritt {
  zeit: string
  was: string
}

const SPRUENGE: Sprungschritt[] = []
const SPRUENGE_MAX = 30

export function merkeSprung(was: string): void {
  SPRUENGE.push({ zeit: new Date().toISOString().slice(11, 23), was })
  if (SPRUENGE.length > SPRUENGE_MAX) SPRUENGE.shift()
}

export function sprungVerlauf(): Sprungschritt[] {
  return [...SPRUENGE]
}

/**
 * Die Spur des Service Workers — **was dort drin geschah, sieht sonst
 * niemand.**
 *
 * Der Mitschrieb oben beginnt erst, wenn die Anwendung läuft. Alles davor —
 * kam die Benachrichtigung überhaupt mit einer Adresse an, was sah
 * `notificationclick`, hat ein offenes Fenster übernommen — geschieht im
 * Worker, und der hat keine Oberfläche. Also wird er gefragt.
 *
 * **Gelesen und nicht weggenommen:** die Spur ist ein Mitschrieb und keine
 * Nachricht. Wer die Diagnose zweimal ansieht, soll dasselbe sehen.
 */
export type Spurlage = 'kein-worker' | 'keine-antwort' | 'leer' | 'da'

export interface Spurbericht {
  lage: Spurlage
  zeilen: Sprungschritt[]
}

/**
 * **Drei Ausgänge, drei Sätze — und vorher waren es drei Ausgänge und ein
 * Satz.**
 *
 * Hier stand `Promise<Sprungschritt[]>`, und „kein Worker", „keine Antwort"
 * und „wirklich nichts aufgeschrieben" ergaben alle dieselbe leere Liste. Auf
 * der Karte stand dann „Der Service Worker hat nichts aufgeschrieben" — auch
 * dann, wenn er in Wahrheit voll war und bloß nicht rechtzeitig antwortete.
 *
 * **Und rechtzeitig hieß 800 Millisekunden.** Ein Service Worker, den das
 * Betriebssystem angehalten hat, muss erst wieder anlaufen; auf dem iPhone ist
 * das keine Seltenheit, sondern der Normalfall. Ein Werkzeug, das genau dann
 * schweigt, wenn man es braucht, ist schlimmer als keines — es sagt
 * überzeugend das Falsche. Auf einer Diagnoseseite wartet niemand auf eine
 * Zehntelsekunde; hier sind drei Sekunden richtig.
 */
export async function workerSpur(): Promise<Spurbericht> {
  if (!('serviceWorker' in navigator)) return { lage: 'kein-worker', zeilen: [] }
  try {
    const anmeldung = await navigator.serviceWorker.ready
    const worker = anmeldung.active
    if (worker === null) return { lage: 'kein-worker', zeilen: [] }
    return await new Promise<Spurbericht>((fertig) => {
      const kanal = new MessageChannel()
      const uhr = window.setTimeout(() => fertig({ lage: 'keine-antwort', zeilen: [] }), 3000)
      kanal.port1.onmessage = (ereignis) => {
        window.clearTimeout(uhr)
        const zeilen = (ereignis.data as { zeilen?: Sprungschritt[] } | null)?.zeilen ?? []
        fertig({ lage: zeilen.length === 0 ? 'leer' : 'da', zeilen })
      }
      worker.postMessage({ typ: 'tb-spur-frage' }, [kanal.port2])
    })
  } catch {
    return { lage: 'keine-antwort', zeilen: [] }
  }
}

/**
 * Ein Ziel, das ankam, bevor jemand zuhörte.
 *
 * Beim Kaltstart wird das Fenster geöffnet *und* bekommt die Nachricht, lange
 * bevor React steht. Ohne diesen Zettel wäre sie weg, und der Tipp führte auf
 * die Startseite — also genau dorthin, wo man nicht hinwollte.
 *
 * Verbraucht heißt gelöscht: ein späteres Neuaufbauen der Oberfläche soll
 * nicht ein zweites Mal wegspringen.
 */
let liegengeblieben: string | null = null

/**
 * Aus der Adresse einen Pfad für den Router machen — oder `null`.
 *
 * **Fremde Herkunft wird nicht gefolgt.** Die Nachricht kommt zwar vom eigenen
 * Service Worker, aber ein Sprung ist ein Sprung: was nicht von hier ist, hat
 * hier nichts zu suchen.
 *
 * **Und der Präfix muss weg.** `main.tsx` baut den `BrowserRouter` ohne
 * `basename`, während der Server mit `Config::basePath()` präfixt. Ungekürzt
 * landete `/travel/t/7` als unbekannter Pfad bei `<Route path="*">` und damit
 * auf der Startseite.
 */
export function alsPfad(url: string): string | null {
  try {
    const ziel = new URL(url, location.href)
    if (ziel.origin !== location.origin) return null
    let pfad = ziel.pathname
    if (BASE !== '' && pfad.startsWith(BASE)) pfad = pfad.slice(BASE.length)
    return (pfad === '' ? '/' : pfad) + ziel.search
  } catch {
    return null
  }
}

/**
 * Zuhören — **vor dem ersten Zeichnen**, in `main.tsx`.
 *
 * **`startMessages()` ist die Zeile, die man vergisst.** Wer wie hier
 * `addEventListener` statt `onmessage` benutzt, bekommt Nachrichten erst nach
 * diesem Aufruf; bis dahin stapeln sie sich stumm. Damit ist auch die
 * Reihenfolge von Registrierung und Übernahme des Controllers gleichgültig:
 * `client.postMessage()` geht an das Fenster, nicht durch den Controller.
 */
export function beobachteTippziel(): void {
  if (!('serviceWorker' in navigator)) return

  navigator.serviceWorker.addEventListener('message', (ereignis) => {
    const daten = ereignis.data as { typ?: string; url?: string } | null
    if (daten?.typ !== 'tb-ziel' || typeof daten.url !== 'string') return

    merkeSprung(`Worker meldet ${daten.url}`)
    const pfad = alsPfad(daten.url)
    if (pfad === null) {
      merkeSprung('… aber die Adresse ist nicht von hier — nichts getan')
      return
    }

    // **Erst antworten, dann springen.** Der Service Worker wartet eine halbe
    // Sekunde auf ein Lebenszeichen; bleibt es aus, nimmt er den alten Weg.
    ereignis.ports[0]?.postMessage({ ok: true })

    if (empfaenger === null) {
      merkeSprung(`… niemand hört zu, ${pfad} bleibt liegen`)
      liegengeblieben = pfad
    } else {
      merkeSprung(`… gesprungen nach ${pfad}`)
      empfaenger(pfad)
    }
  })
  navigator.serviceWorker.startMessages()
}

/**
 * Der Router meldet sich an. Was liegengeblieben ist, kommt sofort — **und
 * beim ersten Mal wird auch der Worker gefragt.**
 *
 * Der Grund steht in `sw.js` an `ZIELSPEICHER`: beim Kaltstart kann der
 * Verweis in der Adresse verlorengehen, weil eine Web-App vom Home-Bildschirm
 * an ihrer `start_url` startet und nicht an der angetippten Adresse. Dann
 * kommt hier keine Nachricht an, es liegt nichts, und die App steht auf der
 * Startseite. Also wird gefragt.
 *
 * **Nur einmal je Aufbau.** Der Verweis gilt einmal, der Worker nimmt ihn beim
 * Beantworten weg; ein zweites Fragen nach jedem Seitenwechsel wäre ohnehin
 * ohne Antwort — und ein Sprung, der später käme, risse einem die Seite unter
 * der Hand weg.
 */
export function abonniereTippziel(wer: Empfaenger): () => void {
  empfaenger = wer
  if (liegengeblieben !== null) {
    const pfad = liegengeblieben
    liegengeblieben = null
    merkeSprung(`Liegengebliebenes Ziel ${pfad} abgeholt`)
    wer(pfad)
  } else if (!gefragt) {
    gefragt = true
    void frageDenWorker(wer)
  }

  /*
   * **Und jedes Mal wieder, wenn die App nach vorn kommt.**
   *
   * Das ist die Lücke, die nach dem Kaltstart übrigblieb. Ist die App schon
   * offen und jemand tippt eine Benachrichtigung an, ruft der Worker das
   * Fenster (`fragWegen`) und wartet **eine halbe Sekunde** auf ein
   * Lebenszeichen. Ein Fenster, das iOS im Hintergrund angehalten hat,
   * antwortet in dieser Zeit nicht — dann nimmt der Worker den alten Weg
   * `fensterchen.navigate?.()`, **und den kennt Safari nicht**. Die App kommt
   * nach vorn und steht, wo sie stand.
   *
   * Das Ziel liegt in diesem Fall aber noch beim Worker: abgeräumt wird es
   * nur, wenn ein Fenster wirklich übernommen hat. Also wird beim
   * Nach-vorn-Kommen noch einmal gefragt.
   *
   * **Ohne Tipp findet das nichts**, und das ist keine Nebensache: der Verweis
   * gilt einmal und nur eine Minute. Wer die App bloß wieder aufnimmt, springt
   * nirgendwohin.
   */
  const beimAuftauchen = () => {
    if (document.visibilityState !== 'visible') return
    void frageDenWorker(wer, 'Beim Nach-vorn-Kommen')
  }
  document.addEventListener('visibilitychange', beimAuftauchen)

  return () => {
    document.removeEventListener('visibilitychange', beimAuftauchen)
    if (empfaenger === wer) empfaenger = null
  }
}

let gefragt = false

/**
 * Beim Worker nachfragen, ob ein angetippter Verweis für uns bereitliegt.
 *
 * **Mit Zeitschranke**, aus demselben Grund wie `fragWegen()` in der anderen
 * Richtung: im Browser kann ein alter Worker stehen, der diese Frage nicht
 * kennt. Bleibt die Antwort aus, war es eben keine.
 */
async function frageDenWorker(wer: Empfaenger, anlass = 'Beim Aufbau'): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  try {
    const anmeldung = await navigator.serviceWorker.ready
    const worker = anmeldung.active
    if (worker === null) return

    const url = await new Promise<string | null>((fertig) => {
      const kanal = new MessageChannel()
      /*
       * **Zweieinhalb Sekunden und nicht 800 Millisekunden.** Ein angehaltener
       * Service Worker muss erst anlaufen; kommt die Antwort zu spät, findet
       * die App das Ziel nie und steht auf der Startseite -- der Fehler, den
       * das hier gerade verhindern soll. Gewartet wird auf einen Sprung, den
       * jemand ausgelöst hat; die halbe Sekunde mehr fällt daneben nicht auf.
       */
      const uhr = window.setTimeout(() => fertig(null), 2500)
      kanal.port1.onmessage = (ereignis) => {
        window.clearTimeout(uhr)
        fertig((ereignis.data as { url?: string | null } | null)?.url ?? null)
      }
      worker.postMessage({ typ: 'tb-ziel-frage' }, [kanal.port2])
    })

    if (url === null) {
      /*
       * **Der leere Fall wird nur beim Aufbau aufgeschrieben.** Seit auch beim
       * Nach-vorn-Kommen gefragt wird, käme diese Zeile bei jedem Wechsel in
       * die App -- nach einem Vormittag stünden dreißig davon da und hätten
       * alles verdrängt, worauf es ankommt.
       */
      if (anlass === 'Beim Aufbau') merkeSprung(`${anlass} gefragt: nichts lag bereit`)
      return
    }
    const pfad = alsPfad(url)
    if (pfad === null) {
      merkeSprung(`${anlass} gefragt: ${url} ist nicht von hier`)
      return
    }
    /*
     * **Steht die App schon dort, wird nicht gesprungen.** Hat der Kaltstart
     * die Adresse doch behalten, ist der abgeholte Verweis dieselbe Stelle —
     * ein `navigate` dorthin wäre folgenlos, aber ein zweiter Eintrag in der
     * Geschichte des Browsers, und der Zurück-Knopf führte im Kreis.
     *
     * **Verglichen wird durch `alsPfad()` und nicht mit `location.pathname`**:
     * der eine Weg hat den Präfix schon abgeschnitten, der andere nicht — und
     * unter `BASE_PATH` wären die beiden nie gleich.
     */
    if (pfad === alsPfad(location.href)) {
      merkeSprung(`${anlass} gefragt: ${pfad} — steht schon da`)
      return
    }
    merkeSprung(`${anlass} gefragt: gesprungen nach ${pfad}`)
    wer(pfad)
  } catch {
    merkeSprung(`${anlass} gefragt: der Worker antwortete nicht`)
  }
}
